/*
 *
 * CalendarSubscription
 *
 */

import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { red } from '@mui/material/colors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Spacings from '~/modules/ui-kit/spacings';
import commonMessages from '~/containers/App/messages';
import styles from './styles.module.css';
import messages from './messages';
import {
  calendarsCreateCalendar,
  calendarsDisableCalendar,
  calendarsGetMyCalendarUrl,
} from '~/generated';
import { extractData } from '~/utils/query-utils';

const CalendarSubscription = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const calendarQuery = useQuery({
    queryKey: ['calendars', 'my', 'url'],
    queryFn: () => extractData(calendarsGetMyCalendarUrl()),
    retry: false,
  });
  const {
    mutate: enableCalendarSubscription,
    isPending: isEnablingCalendarSubscription,
  } = useMutation({
    mutationKey: ['calendars', 'activate'],
    mutationFn: () => extractData(calendarsCreateCalendar()),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['calendars', 'my', 'url'],
      });
      showSnackbar({
        message: intl.formatMessage(messages.enableCalendarSubscriptionSuccess),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.enableCalendarSubscriptionFailure),
      });
    },
  });
  const {
    mutate: disableCalendarSubscription,
    isPending: isDisablingCalendarSubscription,
  } = useMutation({
    mutationKey: ['calendars', 'activate'],
    mutationFn: () => extractData(calendarsDisableCalendar()),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['calendars', 'my', 'url'],
      });
      showSnackbar({
        message: intl.formatMessage(
          messages.disableCalendarSubscriptionSuccess
        ),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(
          messages.disableCalendarSubscriptionFailure
        ),
      });
    },
  });
  const copyCalendarUrlToClipboard = () => {
    if (!calendarQuery.data?.url) {
      return;
    }
    navigator.clipboard.writeText(calendarQuery.data.url);
  };
  return (
    <Spacings.Stack scale="m">
      <Typography variant="h6">
        <FormattedMessage {...messages.calendarSubscription} />
      </Typography>
      {!calendarQuery.isLoading &&
      !calendarQuery.isError &&
      calendarQuery.data &&
      calendarQuery.data.url ? (
        <Spacings.Stack alignItems="stretch" scale="l">
          <Spacings.Stack alignItems="flexStart">
            <textarea
              className={styles.url}
              value={calendarQuery.data.url}
              readOnly
            >
              <Typography>{calendarQuery.data.url}</Typography>
            </textarea>
            <Spacings.Stack alignItems="flexStart">
              <Button variant="contained" onClick={copyCalendarUrlToClipboard}>
                <FormattedMessage {...messages.copyCalendarSubscriptionUrl} />
              </Button>
              <div>
                <Button
                  disabled={isDisablingCalendarSubscription}
                  style={{ color: red[800] }}
                  onClick={() => disableCalendarSubscription()}
                >
                  {isDisablingCalendarSubscription ? (
                    <FormattedMessage {...commonMessages.loading} />
                  ) : (
                    <FormattedMessage
                      {...messages.disableCalendarSubscription}
                    />
                  )}
                </Button>
              </div>
            </Spacings.Stack>
          </Spacings.Stack>
          <Spacings.Stack alignItems="flexStart">
            <Typography variant="subtitle2">
              <FormattedMessage
                {...messages.calendarSubscriptionDescriptionTitle}
              />
            </Typography>
            <Typography variant="body2" component="div">
              <Spacings.Stack>
                <span>
                  <FormattedMessage
                    {...messages.calendarSubscriptionDescription1}
                  />
                </span>
                <span>
                  <FormattedMessage
                    {...messages.calendarSubscriptionDescription2}
                  />
                </span>
              </Spacings.Stack>
              <ul>
                <li>
                  <a href="https://support.google.com/calendar/answer/37100?co=GENIE.Platform%3DDesktop">
                    <FormattedMessage {...messages.googleCalendarLink} />
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/ht202361">
                    <FormattedMessage {...messages.appleIcloudCalendarLink} />
                  </a>
                </li>
                <li>
                  <a href="https://support.office.com/article/import-or-subscribe-to-a-calendar-in-outlook-com-cff1429c-5af6-41ec-a5b4-74f2c278e98c">
                    <FormattedMessage
                      {...messages.microsoftOutlookCalendarLink}
                    />
                  </a>
                </li>
              </ul>
            </Typography>
          </Spacings.Stack>
        </Spacings.Stack>
      ) : (
        <Button
          variant="contained"
          disabled={isEnablingCalendarSubscription}
          onClick={() => enableCalendarSubscription()}
        >
          {isEnablingCalendarSubscription ? (
            <FormattedMessage {...commonMessages.loading} />
          ) : (
            <FormattedMessage {...messages.enableCalendarSubscription} />
          )}
        </Button>
      )}
    </Spacings.Stack>
  );
};

export default CalendarSubscription;
